import type {
  ProductInfoProduct,
  ProductInfoProductSibling
} from '../product-info/ProductInfoProductTypes';
import TooltipFixedTriggerComponent, {
  TooltipImage
} from '@spa/components/tooltips/tooltip-fixed-trigger/tooltip-fixed-trigger.component';
import { AttributeValue } from '@spa/components/product-specifications/AttributeTypes';
import { CapacityUnitCode } from '@spa/lib/converters/convertCapacityUnitCode';
import { ColorTile } from '@spa/components/ProductAssetTypes';
import { ReactElement } from 'react';

export interface SiblingsAttributes {
  readonly manufacturerColorUniqueElements: Map<string, ReactElement>;
  readonly manufacturerColorUniqueNames: Array<string>;
  readonly capacityValueUniqueNames: Array<string>;
  readonly capacityValueUnitCodeMap: Map<string, CapacityUnitCode>;
  readonly capacityValueInnerPackCountMap: Map<string, AttributeValue>;
  readonly clothingSizeUniqueNames: Array<string>;
  readonly inkColorUniqueNames: Array<string>;
  readonly sizeRangeUniqueNames: Array<string>;
  readonly storageUnitUniqueNames: Array<string>;
  readonly storageCapacityUniqueNames: Array<string>;
}

export const extractUniqueAttributes = (
  siblings: ReadonlyArray<ProductInfoProductSibling>,
  {
    storageUnit: currentStorageUnit,
    capacityValue: currentCapacityValue,
    capacityUnitCode: currentCapacityUnitCode,
    innerPackCount: currentInnerPackCount,
    manufacturerColor: currentManufacturerColor
  }: ProductInfoProduct
): SiblingsAttributes => {
  const manufacturerColorUniqueElements: Map<string, ReactElement> = new Map();
  const manufacturerColorUniqueNames: Array<string> = [];
  const capacityValueUniqueNames: Array<string> = [];
  const capacityValueUnitCodeMap: Map<string, CapacityUnitCode> = new Map();
  const capacityValueInnerPackCountMap: Map<string, AttributeValue> = new Map();
  const clothingSizeUniqueNames: Array<string> = [];
  const inkColorUniqueNames: Array<string> = [];
  const sizeRangeUniqueNames: Array<string> = [];
  const storageUnitUniqueNames: Array<string> = [];
  const storageCapacityUniqueNames: Array<string> = [];

  siblings.forEach(
    ({
      manufacturerColorNumber,
      manufacturerColor,
      sizeRange,
      storageCapacity,
      storageUnit,
      clothingSize,
      colorTile,
      inkColor,
      innerPackCount,
      capacityValue,
      capacityUnitCode
    }) => {
      if (
        manufacturerColor !== null &&
        colorTile !== null &&
        !manufacturerColorUniqueNames.includes(manufacturerColor)
      ) {
        const { id, source }: ColorTile = colorTile;
        const colorTitle: string =
          manufacturerColor && manufacturerColorNumber
            ? `${manufacturerColor} - ${manufacturerColorNumber}`
            : manufacturerColor;

        const tooltipImageProps: TooltipImage = {
          height: 45,
          src: source.replace('pdmain', 'original'),
          width: 45
        };

        manufacturerColorUniqueNames.push(manufacturerColor);
        manufacturerColorUniqueElements.set(
          manufacturerColor,
          <TooltipFixedTriggerComponent
            key={id}
            alt={colorTitle}
            image={tooltipImageProps}
            fitContent={true}
            hideMobile={true}
            unoptimized={false}
            isColorTile={true}
          >
            {colorTitle}
          </TooltipFixedTriggerComponent>
        );
      }

      if (
        manufacturerColor === currentManufacturerColor &&
        !capacityValueUniqueNames.includes(capacityValue)
      ) {
        capacityValueUniqueNames.push(capacityValue);
        capacityValueUnitCodeMap.set(capacityValue, capacityUnitCode);
        if (innerPackCount !== null) {
          capacityValueInnerPackCountMap.set(capacityValue, innerPackCount);
        }
      }

      if (clothingSize !== null && !clothingSizeUniqueNames.includes(clothingSize)) {
        clothingSizeUniqueNames.push(clothingSize);
      }

      const isMatchingCurrentCapacity =
        capacityUnitCode === currentCapacityUnitCode && capacityValue === currentCapacityValue;
      if (
        isMatchingCurrentCapacity &&
        inkColor !== null &&
        !inkColorUniqueNames.includes(inkColor)
      ) {
        inkColorUniqueNames.push(inkColor);
      }

      if (sizeRange !== null && !sizeRangeUniqueNames.includes(sizeRange)) {
        sizeRangeUniqueNames.push(sizeRange);
      }

      if (storageUnit !== null && !storageUnitUniqueNames.includes(storageUnit)) {
        storageUnitUniqueNames.push(storageUnit);
      }

      if (
        storageUnit === currentStorageUnit &&
        storageCapacity !== null &&
        !storageCapacityUniqueNames.includes(storageCapacity)
      ) {
        storageCapacityUniqueNames.push(storageCapacity);
      }
    }
  );

  if (currentInnerPackCount !== null && !capacityValueInnerPackCountMap.has(currentCapacityValue)) {
    capacityValueInnerPackCountMap.set(currentCapacityValue, currentInnerPackCount);
  }

  return {
    manufacturerColorUniqueElements,
    manufacturerColorUniqueNames,
    capacityValueUniqueNames,
    capacityValueUnitCodeMap,
    capacityValueInnerPackCountMap,
    clothingSizeUniqueNames,
    inkColorUniqueNames,
    sizeRangeUniqueNames,
    storageUnitUniqueNames,
    storageCapacityUniqueNames
  };
};
